// SS PWA — components/health/HealthStatusBanner.tsx — 2026-06-03
//
// CLIENT-FACING. Shows the locked, owner-friendly status banner. Never
// surfaces rule names, error codes, or internal metrics.

import { bannerFor } from '@/lib/copy/healthOutcomeCopy';

interface HealthStatusBannerProps {
  status: 'green' | 'yellow' | 'red';
}

function Icon({ kind }: { kind: 'check' | 'eye' | 'wrench' }) {
  const common = {
    viewBox: '0 0 24 24',
    width: 28,
    height: 28,
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  if (kind === 'check') {
    return (
      <svg {...common}>
        <path d="M5 12l5 5L20 7" />
      </svg>
    );
  }
  if (kind === 'eye') {
    return (
      <svg {...common}>
        <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    );
  }
  // wrench
  return (
    <svg {...common}>
      <path d="M14.7 6.3a4 4 0 00-5.4 5.4L3 18l3 3 6.3-6.3a4 4 0 005.4-5.4l-3-3-2 2 1 1z" />
    </svg>
  );
}

const toneClasses = {
  green:
    'border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200',
  yellow:
    'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200',
  red:
    'border-red-200 bg-red-50 text-red-900 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200',
};

export function HealthStatusBanner({ status }: HealthStatusBannerProps) {
  const spec = bannerFor(status);
  return (
    <div className={`flex items-center gap-3 rounded-2xl border p-4 ${toneClasses[spec.tone]}`}>
      <div className="shrink-0">
        <Icon kind={spec.icon} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-base font-semibold">{spec.title}</div>
        <div className="text-sm opacity-80">{spec.sub}</div>
      </div>
    </div>
  );
}
